/**
 * Single source of truth for WebRTC capture constraints used by:
 * - gesture-time prewarm (Safari permission persistence)
 * - matrix-js-sdk MediaHandler patch
 *
 * Keep audio/video shapes stable across both paths so cached tracks satisfy SDK requests.
 */

export const CALL_MEDIA_KIND = {
  VOICE: 'voice',
  VIDEO: 'video',
};

/** @param {'voice'|'video'|string} callKind */
export function normalizeCallMediaKind(callKind) {
  return String(callKind || '').toLowerCase() === CALL_MEDIA_KIND.VIDEO
    ? CALL_MEDIA_KIND.VIDEO
    : CALL_MEDIA_KIND.VOICE;
}

export function getSupportedMediaConstraints() {
  try {
    if (typeof navigator === 'undefined') return {};
    return navigator.mediaDevices?.getSupportedConstraints?.() ?? {};
  } catch (_) {
    return {};
  }
}

/** @param {MediaTrackConstraints | boolean | undefined} [base] */
export function buildCallAudioConstraints(base) {
  const supported = getSupportedMediaConstraints();
  const next = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(base && typeof base === 'object' ? base : {}),
  };

  if (supported.channelCount && next.channelCount == null) {
    next.channelCount = { ideal: 1, max: 1 };
  }

  // voiceIsolation omitted — iOS Safari may re-prompt when advanced keys differ between calls.
  return next;
}

/** Resolution/framerate only — never camera selection (deviceId / facingMode). */
export function buildCallVideoQualityConstraints() {
  return {
    width: { ideal: 1280, min: 640 },
    height: { ideal: 720, min: 360 },
    frameRate: { ideal: 30, max: 30 },
  };
}

/**
 * Unwrap `ConstrainDOMString` (`string`, `{ exact }`, `{ ideal }`, arrays).
 * Booleans are ignored — MediaHandler's 3rd argument is a boolean, not a device id.
 * @param {unknown} value
 * @returns {string | null}
 */
export function unwrapConstrainDOMString(value) {
  if (typeof value === 'string' && value) return value;
  if (Array.isArray(value)) {
    const first = value.find((item) => typeof item === 'string' && item);
    return first || null;
  }
  if (!value || typeof value !== 'object') return null;
  const exact = unwrapConstrainDOMString(value.exact);
  if (exact) return exact;
  return unwrapConstrainDOMString(value.ideal);
}

/** @param {MediaStreamConstraints | undefined} constraints */
export function parseRequestedVideoDeviceId(constraints) {
  const video = constraints?.video;
  if (!video || video === true || typeof video !== 'object') return null;
  return unwrapConstrainDOMString(video.deviceId);
}

/** @param {MediaStreamConstraints | undefined} constraints */
export function parseRequestedVideoFacingMode(constraints) {
  const video = constraints?.video;
  if (!video || video === true || typeof video !== 'object') return null;
  return unwrapConstrainDOMString(video.facingMode);
}

/**
 * @param {{ exactDeviceId?: string, facingMode?: string }} [options]
 */
export function buildCallVideoConstraints(options = {}) {
  const { exactDeviceId, facingMode } = options;
  const video = buildCallVideoQualityConstraints();
  const deviceId = typeof exactDeviceId === 'string' && exactDeviceId ? exactDeviceId : null;
  const facing = typeof facingMode === 'string' && facingMode ? facingMode : null;

  if (deviceId) {
    video.deviceId = { exact: deviceId };
  } else if (facing) {
    video.facingMode = { ideal: facing };
  } else {
    video.facingMode = { ideal: 'user' };
  }

  return video;
}

/** @param {'voice'|'video'|string} callKind */
export function buildStableCallMediaConstraints(callKind) {
  const isVideo = normalizeCallMediaKind(callKind) === CALL_MEDIA_KIND.VIDEO;
  return {
    audio: buildCallAudioConstraints(),
    video: isVideo ? buildCallVideoConstraints() : false,
  };
}

/** @param {MediaStreamConstraints | undefined} constraints */
export function parseCaptureNeeds(constraints) {
  if (!constraints || typeof constraints !== 'object') {
    return { audio: false, video: false };
  }
  return {
    audio: !!constraints.audio,
    video: !!constraints.video && constraints.video !== false,
  };
}
